<template>
  <div>
    <!--    Member Form-->
    <validation-observer
        ref="memberForm"
        v-slot="{invalid}"
    >
      <am-modal
          :title="`${updateMemberMode?'Update':'Register'} Member`"
          :show="memberModalOpened"
          :show-overlay="deletingMember"
          size="lg"
          @onClose="closeMemberModal()"
      >
        <!-- Selection/Deletion Controls -->
        <b-row class="align-items-start">
          <div class="col-7">
            <am-select
                v-model="selectedMemberId"
                placeholder="All Members"
                :options="members"
                label-name="full_name"
                value-name="id"
                name="members"
                @input="populateMemberForm"
            />
          </div>
          <div class="col-2">
            <am-dropdown
                icon="TrashIcon"
                name="delete_member"
            >
              <b-dropdown-item
                  id="cd_member"
                  @click="deleteMember()"
              >
                Yes
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item>
                No
              </b-dropdown-item>
            </am-dropdown>
          </div>
          <div class="col-2">
            <am-button
                name="member_action"
                variant="flat-danger"
                icon="XIcon"
                label=""
                tool-tip-text="Clear Form"
                @click="resetMemberForm()"
            />
          </div>
        </b-row>
        <error-display :error="error" />
        <app-collapse
            accordion
            type="margin"
        >
          <app-collapse-item
              id="personal-details"
              title="Personal Details"
          >
            <div class="row">
              <!-- Fields: First Name, Middle Name, Last Name, Gender, DOB, Occupation -->
              <div class="col-md-6">
                <am-input
                    v-model="member.first_name"
                    name="first_name"
                    label="First Name"
                    rules="required"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.middle_name"
                    name="middle_name"
                    label="Middle Name (Optional)"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.last_name"
                    name="last_name"
                    label="Last Name"
                    rules="required"
                />
              </div>
              <div class="col-md-6">
                <am-select
                    v-model="member.gender"
                    value-name="id"
                    label-name="name"
                    name="gender"
                    label="Gender"
                    rules="required"
                    :options="genders"
                />
              </div>
              <div class="col-md-6">
                <am-date-picker
                    id="dob"
                    v-model="member.dob"
                    name="date_of_birth"
                    label="Date of Birth"
                    rules="required|no_future_date"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.occupation"
                    name="other_occupation"
                    label="Other Occupation (Optional)"
                />
              </div>
              <!-- Educational Level Fields -->
              <div class="col-md-6">
                <am-select
                    v-model="member.educational_level"
                    name="educational_level"
                    label="Educational Level"
                    label-name="name"
                    value-name="id"
                    rules="required"
                    :options="educationalLevels"
                />
              </div>
              <div class="col-md-6" v-if="hasOtherEducationalLevel">
                <am-input
                    v-model="member.other_educational_level"
                    name="educational_level_other"
                    label="Other Educational Level"
                    rules="required"
                />
              </div>
              <div class="col-md-6">
                <am-select
                    v-model="member.organization_id"
                    value-name="id"
                    label-name="name"
                    name="society"
                    label="Society"
                    rules="required"
                    :options="organizations"
                />
              </div>
              <!-- RENAMED Date Joined -> Date Joined Society -->
              <div class="col-md-6">
                <am-date-picker
                    id="date_joined_society"
                    v-model="member.date_joined_society"
                    name="date_joined_society"
                    label="Date Joined Society"
                    rules="required|no_future_date"
                />
              </div>
              <!-- Status Field -->
              <div class="col-md-6">
                <am-select
                    v-model="member.status"
                    value-name="id"
                    label-name="name"
                    name="status"
                    label="Status"
                    rules="required"
                    :options="memberStatuses"
                />
              </div>
              
              <!-- Inactive Reason Field -->
              <div v-if="member.status === 'Inactive'" class="col-md-6">
                <am-select
                    v-model="member.inactive_reason"
                    value-name="value"
                    label-name="label"
                    name="inactive_reason"
                    label="Inactive Reason"
                    rules="required"
                    :options="inactiveReasonOptions"
                />
              </div>
              
              <!-- Other Inactive Reason Field -->
              <div v-if="member.status === 'Inactive' && member.inactive_reason === 'other'" class="col-12">
                <am-text-area
                    v-model="member.other_inactive_reason"
                    name="other_inactive_reason"
                    label="Please specify"
                    placeholder="Please specify the reason for making this member inactive..."
                    rules="required"
                    rows="3"
                />
              </div>
              
              <!-- Save Status Button -->
              <div v-if="updateMemberMode" class="col-12 mt-3">
                <am-button
                    label="Save Status"
                    variant="primary"
                    size="sm"
                    :loading="savingStatus"
                    @click="updateMemberStatus"
                />
              </div>
            </div>
          </app-collapse-item>
          <app-collapse-item
              id="contact-details"
              title="Contact Details"
          >
            <div class="row">
              <div class="col-md-6">
                <am-select
                    v-model="member.country_id"
                    name="country"
                    label="Country"
                    rules="required"
                    value-name="id"
                    label-name="name"
                    :options="countries"
                />
              </div>
              <div class="col-md-6">
                <am-select
                    v-model="member.region_id"
                    name="region"
                    label="Region"
                    rules="required"
                    value-name="id"
                    label-name="name"
                    :options="filteredRegions"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.address"
                    name="address"
                    label="Address"
                    rules="required"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.contact"
                    name="contact"
                    label="Contact"
                    rules="required|min_length:10|max_length:15"
                    type="number"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.secondary_contact"
                    name="secondary_contact"
                    label="Secondary Contact (Optional)"
                    rules="min_length:10|max_length:15"
                    type="number"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.email"
                    name="email"
                    label="Email (Optional)"
                    rules="email"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.district"
                    name="district"
                    label="District"
                    rules="required"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.community"
                    name="community"
                    label="Community"
                    rules="required"
                />
              </div>
            </div>
          </app-collapse-item>
          <app-collapse-item
              id="financial-details"
              title="Financial Details (Optional)"
          >
            <div class="row">
              <div class="col-md-6">
                <am-select
                    v-model="member.is_vsla_member"
                    name="is_vsla_member"
                    label="Member of VSLA group"
                    rules="required"
                    label-name="text"
                    value-name="value"
                    :options="yesNoOptions"
                />
              </div>
              <div
                  v-if="member.is_vsla_member === true || member.is_vsla_member === 1 || member.is_vsla_member === '1'"
                  class="col-12"
              >
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <label class="mb-0">Name of VSLA</label>
                  <am-button
                      variant="outline-primary"
                      size="sm"
                      @click="addVslaName"
                  >
                    Add VSLA
                  </am-button>
                </div>
                <div
                    v-for="(vslaName, index) in member.vsla_names"
                    :key="`vsla-${index}`"
                    class="row mb-1"
                >
                  <div class="col-md-10">
                    <am-input
                        v-model="member.vsla_names[index]"
                        :name="`vsla_name_${index}`"
                        label="VSLA Name"
                        rules="required"
                    />
                  </div>
                  <div class="col-md-2 d-flex align-items-end mb-1">
                    <am-button
                        :icon-only="true"
                        icon="TrashIcon"
                        variant="flat-danger"
                        @click="removeVslaName(index)"
                    />
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.bank_name"
                    name="bank_name"
                    label="Bank Name"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.bank_account_no"
                    name="bank_account_no"
                    label="Bank Account No"
                    rules="|min_length:12|max_length:20"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.branch_name"
                    name="branch_name"
                    label="Branch Name"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.momo_no"
                    name="mobile_money_no"
                    label="Momo Number"
                    rules="min_length:10|max_length:15"
                    type="number"
                />
              </div>
            </div>
          </app-collapse-item>
          <app-collapse-item
              id="identity-cards"
              title="Identity Cards"
          >
            <div class="row">
              <div class="col-12 mb-1 d-flex justify-content-between align-items-center">
                <span class="text-muted">Add Identity Cards</span>
                <am-button
                    v-if="member.identity_cards.length < 2"
                    variant="outline-primary"
                    size="sm"
                    @click="addIdentityCard"
                >
                  Add Card
                </am-button>
              </div>
              <div
                  v-for="(card, index) in member.identity_cards"
                  :key="`card-${index}`"
                  class="col-12 border rounded p-1 mb-1"
              >
                <div class="row">
                  <div class="col-md-5">
                    <am-select
                        v-model="card.type"
                        :name="`identity_card_type_${index}`"
                        label="Card Type"
                        rules="required"
                        label-name="text"
                        value-name="value"
                        :options="identityCardOptionsFor(index)"
                    />
                  </div>
                  <div class="col-md-5">
                    <am-input
                        v-model="card.card_number"
                        :name="`identity_card_number_${index}`"
                        label="Card Number"
                        rules="required"
                    />
                  </div>
                  <div class="col-md-2 d-flex align-items-end mb-1">
                    <am-button
                        :icon-only="true"
                        icon="TrashIcon"
                        variant="flat-danger"
                        @click="removeIdentityCard(index)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </app-collapse-item>
          <app-collapse-item
              id="next-of-kin-details"
              title="Household & Next of Kin"
          >
            <div class="row">
              <div class="col-md-6">
                <am-select
                    v-model="member.marital_status"
                    name="marital_status"
                    label="Marital Status"
                    rules="required"
                    label-name="name"
                    value-name="id"
                    :options="maritalStatuses"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.no_of_children_high_school"
                    name="no_of_children_high_school"
                    label="No. of Children High School"
                    rules="required|numeric|min_value:0"
                    type="number"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.no_of_children_tertiary"
                    name="no_of_children_tertiary"
                    label="No. of Children Tertiary"
                    rules="required|numeric|min_value:0"
                    type="number"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.no_of_other_children"
                    name="no_of_other_children"
                    label="Other Children"
                    rules="required|numeric|min_value:0"
                    type="number"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    :value="totalChildren"
                    name="no_of_children"
                    label="Total No. of Children"
                    :readonly="true"
                    :suffix-icon="null"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.beneficiary_full_name"
                    name="beneficiary_full_name"
                    label="Beneficiary Full Name"
                    rules="required"
                />
              </div>
              <div class="col-md-6">
                <am-input
                    v-model="member.beneficiary_phone"
                    name="beneficiary_phone"
                    label="Beneficiary Phone"
                    rules="required|min_length:10|max_length:15"
                    type="number"
                />
              </div>
            </div>
          </app-collapse-item>
        </app-collapse>
        <!-- Photo Upload / Capture Section -->
        <div class="row mt-2">
          <div class="col-12 mb-1">
            <am-file-button
                label="Upload Photo"
                variant="outline-primary"
                @onFileUpload="handleFileUpload"
            />
          </div>
          <div
              v-if="memberPhoto"
              class="col-12 mt-1 mb-2"
          >
            <label>Member Photo</label>
            <div class="d-flex align-items-center">
              <am-avatar-data-url
                  :size="60"
                  :src="memberPhoto"
              />
              <div class="mr-1" />
              <am-button
                  :icon-only="true"
                  icon="XIcon"
                  variant="flat-danger"
                  @click="removePhoto()"
              />
            </div>
          </div>
          <div class="col-12">
            <b-form-checkbox
                v-model="showCapture"
                class="mb-1"
            >
              Capture Photo
            </b-form-checkbox>
          </div>
            <div v-if="showCapture" style="width: 100% !important;" class="d-flex flex-column align-items-center justify-content-center">
              <web-cam
                  ref="webCam"
                  style="border:5px greenyellow solid;border-radius:5px;background-color: green;"
                  :height="350"
                  :width="350"
                  :select-first-device="true"
                  :resolution="{ height: 500, width: 500 }"
                  @started="onCameraStart"
                  @cameras="setCameras"
              />
              <div
                  class="mt-1"
                  style="width: 350px;"
              >
                <am-select
                    :options="cameras"
                    label-name="label"
                    value-name="deviceId"
                    placeholder="Select Camera"
                    @input="changeCamera"
                />
              </div>
              <div class="d-flex justify-content-center ">
                <am-button
                    :disabled="!streamHasStarted"
                    variant="flat-success"
                    label="Capture"
                    icon="CameraIcon"
                    @click="onClick()"
                />
                <div class="mr-1" />
                <am-button
                    :disabled="!streamHasStarted"
                    label="Stop"
                    variant="flat-danger"
                    icon="StopCircleIcon"
                    @click="stopCamera()"
                />
              </div>
            </div>
        </div>
        <template slot="modal-actions">
          <b-row class="float-right">
            <am-button
                name="cancel"
                variant="flat-danger"
                icon="XIcon"
                class="mr-1"
                label="Cancel"
                @click="closeMemberModal()"
            />
            <am-button
                name="save_member"
                :label="updateMemberMode?'Update':'Register'"
                :disabled="invalid"
            :loading="memberLoading"
            loading-text="Loading..."
            @click="updateMemberMode?updateMember():createMember()"
            />
          </b-row>
        </template>
      </am-modal>
    </validation-observer>

    <!-- Cropper Modal -->
    <am-modal
        title="Crop Image"
        :show="showCropper"
        size="md"
        @onClose="onCropperClosed()"
    >
      <cropper
          class="cropper"
          :src="memberPhoto"
          :stencil-props="{
          aspectRatio: 1/1
        }"
          @change="getImage"
      />
      <template slot="modal-actions">
        <b-row class="float-right">
          <am-button
              variant="flat-danger"
              icon="XIcon"
              class="mr-1"
          label="Cancel"
          @click="onCropperClosed()"
          />
          <div style="margin-right: 10px;" />
          <am-button
              label="Save Image"
              @click="saveCroppedImage()"
          />
        </b-row>
      </template>
    </am-modal>
  </div>
</template>

<script>
import {
  BRow, BDropdownItem, BDropdownDivider, BFormCheckbox,
} from 'bootstrap-vue'
import { serialize } from 'object-to-formdata'
import { ValidationObserver } from 'vee-validate'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import logData from '@/libs/log'
import AmInput from '@/farmbusiness/components/AmInput.vue'
import AmDatePicker from '@/farmbusiness/components/AmDatePicker.vue'
import AmSelect from '@/farmbusiness/components/AmSelect.vue'
import AmFileButton from '@/farmbusiness/components/AmFileButton.vue'
import AmButton from '@/farmbusiness/components/AmButton.vue'
import AmDropdown from '@/farmbusiness/components/AmDropdown.vue'
import AmModal from '@/farmbusiness/components/AmModal.vue'
import showToast from '@/farmbusiness/mixins/showToast'
import ErrorDisplay from '@/farmbusiness/components/AmErrorDisplay.vue'
import { WebCam } from 'vue-web-cam'
import { Cropper } from 'vue-advanced-cropper'
import urlToFile from '@/farmbusiness/mixins/urlToFile'
// Removed import of defaultMember 'mem' as structure is defined in data() now
// import mem from './defaultMember'
import 'vue-advanced-cropper/dist/style.css'
import AmAvatarDataUrl from '@/farmbusiness/components/AmAvatarDataUrl.vue'

export default {
  name: 'MemberSetup',
  components: {
    AmAvatarDataUrl,
    ErrorDisplay,
    AmModal,
    AmDropdown,
    AmButton,
    AmFileButton,
    AmSelect,
    AmDatePicker,
    AmInput,
    ValidationObserver,
    BRow,
    BDropdownItem,
    BDropdownDivider,
    AppCollapse,
    AppCollapseItem,
    Cropper,
    WebCam,
    BFormCheckbox,
  },
  mixins: [showToast, urlToFile],
  props: {
    modalOpened: {
      type: Boolean,
      default: false,
    },
    id: {
      type: Number, // Allow Number | String | null if IDs can be non-numeric
      default: null,
    },
  },
  data() {
    return {
      error: {},
      memberModalOpened: false,
      updateMemberMode: false,
      memberLoading: false,
      deletingMember: false,
      selectedMemberId: null,
      member: { // Updated base structure
        id: null,
        first_name: null,
        middle_name: null,
        last_name: null,
        gender: null,
        dob: null,
        occupation: null,
        educational_level: null,
        other_educational_level: null,
        organization_id: null, // Represents Society ID
        date_joined_society: null, // Renamed frontend field
        status: null,
        address: null,
        contact: null,
        secondary_contact: null,
        email: null,
        country_id: null,
        region_id: null,
        district: null,
        community: null,
        bank_name: null,
        bank_account_no: null,
        branch_name: null,
        momo_no: null,
        is_vsla_member: false,
        vsla_names: [],
        identity_cards: [],
        marital_status: null,
        no_of_children_high_school: 0,
        no_of_children_tertiary: 0,
        no_of_other_children: 0,
        beneficiary_full_name: null,
        beneficiary_phone: null,
        photo: null, // Holds the File object for submission
        delete_photo: false,
        inactive_reason: null,
        other_inactive_reason: null,
      },
      savingStatus: false,
      memberPhoto: null, // Holds the DataURL/URL for preview
      triggerRegionsReload: false, // Consider removing if not used

      // Camera/Cropper State
      streamHasStarted: false,
      showCapture: false,
      showCropper: false,
      cameras: [],
      canvas: null, // Holds the cropper canvas

      // Static Dropdown Options
      yesNoOptions: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      identityCardTypeOptions: [
        { text: 'Ghana Card', value: 'ghana_card' },
        { text: 'COCOBOD Card', value: 'cocobod_card' },
      ],
      memberStatuses: [
        { id: 'Active', name: 'Active' },
        { id: 'Inactive', name: 'Inactive' },
      ],
      inactiveReasonOptions: [
        { value: 'business_close', label: 'Business Close' },
        { value: 'non_compliant', label: 'Non-compliant' },
        { value: 'no_longer_working_with_association', label: 'No longer working with association' },
        { value: 'duplicate_record', label: 'Duplicate record' },
        { value: 'merged_with_another_organization', label: 'Merged with another organization' },
        { value: 'ownership_changed', label: 'Ownership changed' },
        { value: 'sanctioned_by_association', label: 'Sanctioned by association' },
        { value: 'contract_expired', label: 'Contract expired' },
        { value: 'other', label: 'Other (please specify)' }
      ],
      educationalLevels: [
        { id: 'none', name: 'None'}, // Added None based on potential need
        { id: 'primary', name: 'Primary' },
        { id: 'secondary', name: 'Secondary' },
        { id: 'tertiary', name: 'Tertiary' },
        { id: 'other', name: 'Other' },
      ],
      genders: [
        { id: 'male', name: 'Male' },
        { id: 'female', name: 'Female' },
      ],
      maritalStatuses: [
        { id: 'single', name: 'Single' },
        { id: 'married', name: 'Married' },
        { id: 'divorced', name: 'Divorced' },
        { id: 'widowed', name: 'Widowed' } // Added Widowed
      ],
      countries: [],
    }
  },
  mounted() {
    this.fetchCountries();
  },
  computed: {
    // Computed property for country ID change detection (triggers region reset)
    selectedCountry() { // This approach can sometimes cause issues if region is set too quickly
      return this.countries.find((c)=>c.id===this.member.country_id)
    },
    organizations() {
      // Ensure getter returns an array
      return this.$store.getters['organization/organizations'] || [];
    },
    hasOtherEducationalLevel() {
      return this.member.educational_level === 'other';
    },
    totalChildren() {
      return (Number(this.member.no_of_children_high_school) || 0)
        + (Number(this.member.no_of_children_tertiary) || 0)
        + (Number(this.member.no_of_other_children) || 0)
    },
    // photoInvalid() { // REMOVED as photo is optional now
    //   return false; // Always return false, validation removed from button
    // },
    selectedMember() {
      if (!this.selectedMemberId) return null;
      // Ensure getter returns an array
      const membersList = this.$store.getters['member/members'] || [];
      const member = membersList.find(o => o.id === this.selectedMemberId);
      return member ? { ...member } : null; // Return a copy or null
    },
    members() {
      // Ensure getter returns an array
      return this.$store.getters['member/members'] || [];
    },
    regions() {
      if(this.selectedCountry){
        return  this.selectedCountry.regions
      }
      // Ensure getter returns an array
      return []
    },
    filteredRegions() {
      // Ensure regions is an array and country_id is valid before filtering
      if (!Array.isArray(this.regions) || !this.member.country_id) {
        return [];
      }
      // Ensure comparison handles string/number type differences if necessary
      const currentCountryId = Number(this.member.country_id); // Or String()
      return this.regions.filter(re => Number(re.country_id) === currentCountryId);
    },
  },
  watch: {
    // Watch country_id to reset region_id
    'member.country_id'(newCountryId, oldCountryId) {
      // Only reset if the value actually changes and wasn't null initially
      if (newCountryId !== oldCountryId && oldCountryId !== undefined) {
        this.member.region_id = null;
      }
    },
    modalOpened(opened) {
      this.memberModalOpened = opened;
      // Reset form when modal opens without a pre-selected ID
      if (opened && !this.id) {
        this.resetMemberForm();
      }
      // Populate form if modal opens WITH an ID
      if (opened && this.id) {
        this.selectedMemberId = this.id; // Ensure selected ID matches prop
        this.populateMemberForm(); // Populate based on the ID
      }
      // Stop camera when modal closes
      if (!opened && this.$refs.webCam && this.streamHasStarted) {
        this.stopCamera();
      }
    },
    // Watch the ID prop directly to handle external changes
    id(newId) {
      this.selectedMemberId = newId;
      // Re-populate or reset if the modal is already open when ID changes
      if (this.memberModalOpened) {
        if (newId) {
          this.populateMemberForm();
        } else {
          this.resetMemberForm();
        }
      }
    },
  },
  methods: {
    fetchCountries(){
      this.$http.get('system/countries').then((res)=>{
        this.countries = res.data
      })
    },
    onCameraStart(stream) { // Added stream argument if needed
      this.streamHasStarted = true;
    },
    changeCamera(deviceId) {
      if (deviceId && this.$refs.webCam) {
        this.$refs.webCam.changeCamera(deviceId);
      }
    },
    handleFileUpload(upload) {
      this.memberPhoto = upload.dataUrl; // Set preview
      // Keep the raw file temporarily if needed before cropping
      // this.rawFile = upload.file;
      this.showCropper = true; // Show cropper
      this.showCapture = false; // Hide camera if upload is used
      if (this.$refs.webCam && this.streamHasStarted) this.stopCamera(); // Stop camera
    },
    removePhoto() {
      this.member.photo = null; // Clear the file object in the model
      this.member.delete_photo = true; // Set flag for backend
      this.memberPhoto = null; // Clear preview
      this.canvas = null; // Clear cropper canvas if present
    },
    stopCamera() {
      if (this.$refs.webCam) {
        this.$refs.webCam.stop();
      }
      this.streamHasStarted = false;
      this.showCapture = false; // Optionally hide the camera view on stop
    },
    onCropperClosed() {
      this.showCropper = false;
      // If user cancels cropper after upload, potentially clear the photo?
      // if (!this.member.photo) { // Check if saveCroppedImage was called
      //   this.memberPhoto = null; // Clear preview if cancelled
      // }
    },
    saveCroppedImage() {
      if (this.canvas) {
        // Convert cropped canvas to Data URL for preview
        this.memberPhoto = this.canvas.toDataURL();
        // Convert Data URL to File object for submission
        this.member.photo = this.urlToFile(this.memberPhoto, `member_photo_${Date.now()}.png`); // Use helper mixin
        this.member.delete_photo = false; // Ensure delete flag is false when saving new photo
        // Stop camera if it was running
        if (this.$refs.webCam && this.streamHasStarted) {
          this.stopCamera();
        }
        this.showCapture = false; // Hide camera view
        this.showCropper = false; // Close cropper modal
      } else {
        this.showError('Could not get cropped image data.');
      }
    },
    getImage({ canvas }) { // Destructure event to get canvas directly
      this.canvas = canvas;
    },
    onClick() { // Capture from Webcam
      if (this.$refs.webCam) {
        this.memberPhoto = this.$refs.webCam.capture(); // Get DataURL from webcam
        this.showCropper = true; // Open cropper
      } else {
        this.showError('Webcam not available.');
      }
    },
    setCameras(cameras) {
      this.cameras = cameras || [];
    },

    // --- Form Management Methods ---
    resetMemberForm() {
      // Reset the member object to its initial state
      this.member = {
        id: null, first_name: null, middle_name: null, last_name: null, gender: null,
        dob: null, occupation: null, educational_level: null, other_educational_level: null,
        organization_id: null, date_joined_society: null, status: null, address: null,
        contact: null, secondary_contact: null, email: null, country_id: null, region_id: null,
        district: null, community: null, bank_name: null, bank_account_no: null, branch_name: null,
        momo_no: null, is_vsla_member: false, vsla_names: [], identity_cards: [],
        marital_status: null, no_of_children_high_school: 0, no_of_children_tertiary: 0,
        no_of_other_children: 0, beneficiary_full_name: null,
        beneficiary_phone: null, photo: null, delete_photo: false,
        inactive_reason: null, other_inactive_reason: null,
      };
      this.selectedMemberId = null;
      this.updateMemberMode = false;
      this.memberPhoto = null; // Clear preview
      this.error = {}; // Clear errors
      this.showCapture = false; // Hide camera capture section
      this.canvas = null; // Clear cropper canvas

      // Stop camera if running
      if (this.$refs.webCam && this.streamHasStarted) {
        this.stopCamera();
      }
      // Reset validation state
      this.$nextTick(() => {
        if (this.$refs.memberForm) {
          this.$refs.memberForm.reset();
        }
      });
    },
    addVslaName() {
      if (!Array.isArray(this.member.vsla_names)) {
        this.$set(this.member, 'vsla_names', [])
      }
      this.member.vsla_names.push('')
    },
    removeVslaName(index) {
      this.member.vsla_names.splice(index, 1)
    },
    addIdentityCard() {
      if (!Array.isArray(this.member.identity_cards)) {
        this.$set(this.member, 'identity_cards', [])
      }
      if (this.member.identity_cards.length >= 2) return
      const used = this.member.identity_cards.map(c => c.type)
      const nextType = this.identityCardTypeOptions.find(o => !used.includes(o.value))
      this.member.identity_cards.push({
        id: null,
        type: nextType ? nextType.value : null,
        card_number: null,
      })
    },
    removeIdentityCard(index) {
      const card = this.member.identity_cards[index]
      if (card && card.id && this.member.id) {
        this.$store.dispatch('member/deleteIdentityCard', {
          memberId: this.member.id,
          cardId: card.id,
        }).then(() => {
          this.member.identity_cards.splice(index, 1)
          this.showSuccess('Identity card removed')
        }).catch(() => {
          this.showError('Failed to remove identity card')
        })
        return
      }
      this.member.identity_cards.splice(index, 1)
    },
    identityCardOptionsFor(index) {
      const selected = this.member.identity_cards[index]?.type
      const usedElsewhere = this.member.identity_cards
        .filter((_, i) => i !== index)
        .map(c => c.type)
        .filter(Boolean)
      return this.identityCardTypeOptions.filter(o => o.value === selected || !usedElsewhere.includes(o.value))
    },
    populateMemberForm() {
      // Use the computed property to get the selected member data
      const currentSelectedMember = this.selectedMember;

      if (currentSelectedMember) {
        this.updateMemberMode = true;
        // Map fetched data to the local member model
        this.member = {
          ...currentSelectedMember, // Spread fetched data
          // Explicitly map renamed fields from the source (currentSelectedMember)
          date_joined_society: currentSelectedMember.date_joined,
          momo_no: currentSelectedMember.mobile_money_no,
          is_vsla_member: !!currentSelectedMember.is_vsla_member,
          vsla_names: Array.isArray(currentSelectedMember.vsla_names)
            ? [...currentSelectedMember.vsla_names]
            : [],
          identity_cards: Array.isArray(currentSelectedMember.identity_cards)
            ? currentSelectedMember.identity_cards.map(c => ({
              id: c.id,
              type: c.type,
              card_number: c.card_number,
            }))
            : [],
          no_of_children_high_school: currentSelectedMember.no_of_children_high_school ?? 0,
          no_of_children_tertiary: currentSelectedMember.no_of_children_tertiary ?? 0,
          no_of_other_children: currentSelectedMember.no_of_other_children ?? 0,
          // Ensure fields not in the form model are removed
          date_joined: undefined,
          // Handle photo separately
          photo: null, // Don't copy the File/URL to the model's photo property
          delete_photo: false, // Reset delete flag on population
        };
        // Set the photo preview URL
        this.memberPhoto = currentSelectedMember.photo; // Assuming 'photo' holds the URL

        // Handle dependent dropdowns (Region based on Country)
        const countryId = currentSelectedMember.country_id;
        const regionIdToSet = currentSelectedMember.region_id;
        // Set country first, then use nextTick to set region after country update propagates
        this.member.country_id = countryId;
        this.$nextTick(() => {
          this.member.region_id = regionIdToSet;
        });

      } else {
        // If no member found for the ID, reset the form
        this.resetMemberForm();
      }
      // Reset validation state after population
      this.$nextTick(() => {
        if (this.$refs.memberForm) {
          this.$refs.memberForm.reset();
        }
      });
    },
    closeMemberModal() {
      this.memberModalOpened = false;
      this.$emit('modalClosed');
      this.resetMemberForm(); // Reset form state when closing
    },
    // Helper to prepare payload for submission
    prepareMemberPayload() {
      const payload = { ...this.member};

      // Map renamed field back to backend expectation ('date_joined')
      payload.date_joined = payload.date_joined_society;
      delete payload.date_joined_society; // Remove frontend-specific key
      payload.mobile_money_no = payload.momo_no;
      delete payload.momo_no;
      payload.is_vsla_member = !!(payload.is_vsla_member === true || payload.is_vsla_member === 1 || payload.is_vsla_member === '1');
      if (!payload.is_vsla_member) {
        payload.vsla_names = [];
      } else {
        payload.vsla_names = (payload.vsla_names || []).filter(n => !!n);
      }
      payload.identity_cards = (payload.identity_cards || []).filter(c => c.type && c.card_number);
      // Ensure 'photo' is the File object or handle deletion flag
      if (!(payload.photo instanceof Blob)) {
        // If photo wasn't set/changed AND delete flag is not true, remove photo key
        if (!payload.delete_photo) {
          delete payload.photo;
        }
      }


      // Remove any temporary/internal fields if necessary
      delete payload.other_educational_level_display; // Example
      delete payload.no_of_children;

      // Use FormData serialization
      return serialize(payload, { indices: true, booleansAsIntegers: true });
    },
    createMember() {
      // Validate form before proceeding (optional explicit check)
      this.$refs.memberForm.validate().then(success => {
        if (!success) {
          this.showError('Please fix the errors in the form.');
          return;
        }

        // --- Photo Check (Optional but Recommended) ---
        // if (!this.member.photo && !this.updateMemberMode) { // Check only on create if desired
        //     this.showError('Please upload or capture a photo for the new member.');
        //     return;
        // }
        // --- End Photo Check ---


        this.error = {};
        this.memberLoading = true;
        const payload = this.prepareMemberPayload();

        this.$store.dispatch('member/createMember', payload)
            .then(res => {
              this.showSuccess('Created member successfully');
              this.resetMemberForm();
              this.$emit('created'); // Notify parent
            })
            .catch(err => {
              logData(err);
              this.error = err.errors || { detail: err.message || 'An unknown error occurred.' };
              this.showError('Failed to create member');
            }).finally(() => {
          this.memberLoading = false;
        });
      });
    },
    updateMember() {
      this.$refs.memberForm.validate().then(success => {
        if (!success) {
          this.showError('Please fix the errors in the form.');
          return;
        }
        if (!this.member.id) {
          this.showError('Cannot update: Member ID is missing.');
          return;
        }

        this.error = {};
        this.memberLoading = true;
        const payload = this.prepareMemberPayload();

        this.$store.dispatch('member/updateMember', { member: payload, id: this.member.id })
            .then(res => {
              this.showSuccess('Updated member successfully');
              this.$emit('updated'); // Notify parent
              // Optionally close modal or refresh data
              // this.closeMemberModal();
            })
            .catch(err => {
              logData(err);
              this.error = err.errors || { detail: err.message || 'An unknown error occurred.' };
              this.showError('Failed to update member');
            }).finally(() => {
          this.memberLoading = false;
        });
      });
    },
    updateMemberStatus() {
      if (!this.member.id) {
        this.showError('Cannot update: Member ID is missing.');
        return;
      }

      this.savingStatus = true;
      
      const updateData = {
        memberId: this.member.id,
        status: this.member.status,
        inactive_reason: this.member.status === 'Inactive' ? this.member.inactive_reason : null,
        other_inactive_reason: (this.member.status === 'Inactive' && this.member.inactive_reason === 'other') ? this.member.other_inactive_reason : null
      };

      this.$store.dispatch('member/updateMemberStatus', updateData)
        .then(() => {
          this.showSuccess(`Member status updated to ${this.member.status}`);
          this.$emit('updated'); // Notify parent to refresh data
        })
        .catch((err) => {
          this.showError(`Failed to update member status: ${err.message}`);
        })
        .finally(() => {
          this.savingStatus = false;
        });
    },
    deleteMember() {
      if (!this.selectedMemberId) {
        this.showInfo('Please select a member to delete');
        return;
      }
      this.deletingMember = true;
      this.$store.dispatch('member/deleteMember', this.selectedMemberId)
          .then(() => {
            this.showSuccess('Deleted Successfully');
            this.resetMemberForm();
            this.$emit('deleted'); // Notify parent
          })
          .catch((err) => {
            logData(err);
            this.showError('Failed to delete member');
            this.error = { detail: err.message || 'Could not delete the selected member.' };
          }).finally(() => {
        this.deletingMember = false;
      });
    },
  }
}
</script>

<style scoped>
/* Add styles for cropper or other elements if needed */
.cropper {
  max-height: 400px; /* Example max height */
  background: #DDD;
}
</style>